import { useState } from 'react';
import toast from 'react-hot-toast';

import { DsButton, ModalDialog } from './ds';
import { ReactComponent as GithubIcon } from '../assets/images/github-logo.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { openGithubPermissions } from '../utils/githubPopup';
import back4app2 from '../back4app2';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';

/**
 * Offered when a deploy failed because the GitHub App installation is gone.
 *
 * This is the one deployment failure the customer can fix themselves in about
 * ten seconds, and until now the only trace of it was a line in the log panel
 * — `github app installation is not valid anymore`, under `deployment failed`,
 * with nothing on screen saying that re-granting access is the fix or where to
 * do it. People opened support tickets for it.
 *
 * It redeploys on its own afterwards, and that is the point: someone who has
 * just re-granted access wants the build that failed, not a second decision.
 *
 * But only once access is CONFIRMED. The window closing means the window
 * closed — the person may have granted access or changed their mind and hit
 * the X, and those are indistinguishable from here. So on close it asks GitHub
 * for the repository list and looks for this app's repository in it: that is
 * the exact capability the deploy needs, so finding it there is proof, and not
 * finding it is proof of the opposite.
 *
 * The alternative — retry blindly and let it fail — would have burned a
 * deployment to ask a question this answers for free, and left the customer
 * reading a second identical failure.
 */
const GitHubReconnectModal = ({ open, onClose, serviceEnvironmentId, repositoryId, repositoryFullName }: {
  open: boolean;
  onClose: () => void;
  serviceEnvironmentId?: string;
  /** What the access probe runs against. Without it the modal cannot verify
   * and falls back to retrying blind. */
  repositoryId?: string;
  repositoryFullName?: string;
}) => {
  const [isReconnecting, setIsReconnecting] = useState(false);

  const [notGranted, setNotGranted] = useState(false);

  const onReconnect = () => {
    trackEvent(AmplitudeEvent.EDIT_GITHUB_PERMISSION_CLICK);
    setIsReconnecting(true);
    setNotGranted(false);

    openGithubPermissions({
      onClosed: async () => {
        try {
          // Asking for the branches, because that is the ONE call that
          // reproduces what the deploy does: the resolver claims an
          // installation access token and hits GitHub with it, which is
          // exactly where the deploy died. If it answers, a retry will get
          // past the step that failed.
          //
          // The obvious check — "is the repository still in the list?" — was
          // tried first and verifies nothing: `gitHubRepositories` reads our
          // OWN database, so a repository whose access was revoked minutes ago
          // is still listed as accessible. It said yes, the deploy fired, and
          // it failed exactly as before.
          if (!repositoryId) {
            // Nothing to probe with. Retry and let the deploy be the check —
            // worse than asking, better than leaving the customer stuck.
            if (serviceEnvironmentId) {
              await back4app2.triggerManualDeployment(serviceEnvironmentId);
            }

            onClose();
            return;
          }

          try {
            await back4app2.repositoryBranches(repositoryId);
          } catch (accessError) {
            setNotGranted(true);
            return;
          }

          if (serviceEnvironmentId) {
            await back4app2.triggerManualDeployment(serviceEnvironmentId);
          }

          onClose();
        } catch (e) {
          // The check itself failed — a network blip, or the session expired
          // while the popup was open. Not the same as "access was not
          // granted", so it does not claim that.
          toast.error(
            'We could not check the GitHub access. Please try again.',
            {
              className:
                'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] min-w-xs text-sm',
              icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
              duration: 6000,
            }
          );
        } finally {
          setIsReconnecting(false);
        }
      },
    });
  };

  return (
    // The house dialog: navy panel, title left, buttons right. It was the light
    // centred pattern until the container screens acquired one of their own —
    // a warm grey panel on a navy page was the last surface that still read as
    // a different product.
    <ModalDialog
      open={open}
      onClose={onClose}
      icon={<GithubIcon width="20px" height="20px" className="text-navy-100" />}
      title="Back4app lost access to this repository"
      /* The message changes once we have looked. Saying "grant access again"
         to someone who just came back from doing exactly that, without
         acknowledging that we checked and did not find it, is the kind of loop
         people give up on. */
      blurb={notGranted
        ? 'We still cannot see it. On the GitHub page, make sure it is included in the repositories Back4app can access — then try again.'
        : 'The GitHub App installation was removed or its access changed. Grant access again and we will retry the deployment for you.'}
      width="30rem"
      footer={<>
        <DsButton onClick={onClose} isDisabled={isReconnecting}>Not now</DsButton>
        <DsButton variant="action" onClick={onReconnect} isDisabled={isReconnecting}>
          {isReconnecting
            ? 'Checking access…'
            : notGranted
              ? 'Open GitHub again'
              : 'Grant access and retry'}
        </DsButton>
      </>}
    >
      {/* On its own line, in mono. Inline in the sentence a long repository
          name broke mid-word across two lines and buried the one detail the
          customer needs to recognise which app this is about. */}
      {repositoryFullName ? (
        <span className="font-courrier text-ds-115 text-navy-100 break-all">
          {repositoryFullName}
        </span>
      ) : null}
    </ModalDialog>
  );
};

export default GitHubReconnectModal;
